/*
 * FICHIER: Navbar.js
 * BUT: Barre de navigation principale de l'application
 * 
 * Cette barre de navigation contient :
 * - Logo cliquable vers l'accueil
 * - Menu desktop (horizontal) pour grands écrans
 * - Menu mobile (slide latéral) pour petits écrans
 * - Barre de recherche globale
 * - Gestion responsive et animations
 * 
 * Utilise des styled-components pour un styling avancé et responsive
 */

// Import des hooks React de base
import React, { useState, useEffect, useRef } from 'react';

// Import de styled-components pour le CSS-in-JS
import styled, { keyframes, css } from 'styled-components';

// Import des outils de navigation React Router
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

// Import des icônes FontAwesome
import { 
  FaBars,           // Icône hamburger (menu mobile)
  FaTimes,          // Icône fermer (X)
  FaSearch,         // Icône recherche
  FaNewspaper,      // Icône actualités
  FaCalendarAlt,    // Icône événements
  FaUsers,          // Icône clubs
  FaBook,           // Icône bibliothèque
  FaHandshake,      // Icône partenaires
  FaFileAlt,        // Icône document
  FaFile,           // Icône fichier
  FaArrowsAltH      // Icône flèches horizontales
} from 'react-icons/fa';

// Import de Framer Motion pour les animations
import { motion, AnimatePresence } from 'framer-motion';

// Import des composants de popup
import AboutPopupSidebar from '../popups/AboutPopupSidebar';

// Import de la fonction API pour récupérer les données d'accueil (logo)
import { getAccueil, searchAllEntities } from '../../api/strapi';
import { containsNormalized } from '../../utils/stringUtils';

// Import du logo par défaut
import LogoOsarc from '../../assets/LogoOsarc.png';
// Logo Club du mois (sondage) - même que le bouton flottant PC
import ClubDuMoisLogo from '../../assets/Club du mois.svg';

// Import du contexte de navigation personnalisé
import { useNavigation } from '../../contexts/NavigationContext';

// Importer les popups d'activités depuis ArticleCard
import ArticleCard from '../common/ArticleCard';

// Import de la détection d'appareil
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// Import du service sondage pour savoir si un sondage est en cours
import SimplePollService from '../../services/SimplePollService';

/*
 * =================================================================================
 * STYLED COMPONENTS - Composants stylés avec CSS-in-JS
 * =================================================================================
 */

// Conteneur principal de l'en-tête (header)
const HeaderContainer = styled.header`
  position: fixed;                     /* Position fixe pour rester en haut */
  top: 0;
  left: 0;
  width: 100%;
  z-index: var(--z-fixed);            /* Z-index défini dans les variables CSS */
  background-color: var(--white);     /* Fond blanc */
  box-shadow: var(--shadow-sm);       /* Ombre légère */
  transition: all var(--transition-normal); /* Animation fluide */

  /* Classe ajoutée dynamiquement quand on scroll */
  &.scrolled {
    box-shadow: var(--shadow-md);     /* Ombre plus marquée quand on scroll */
  }
`;

// Conteneur de navigation (largeur limitée, centrée)
const NavContainer = styled.div`
  display: flex;                      /* Layout flexbox */
  justify-content: space-between;     /* Espace entre logo et menu */
  align-items: center;                /* Alignement vertical centré */
  padding: 0.5rem var(--space-lg);    /* Espacement interne */
  max-width: var(--container-xl);     /* Largeur maximale */
  margin: 0 auto;                     /* Centrage horizontal */
  min-width: 0;                       /* Permet la compression */
  overflow: visible;                  /* S'assure que rien n'est caché */

  /* RESPONSIVE - Tablettes */
  @media (max-width: 768px) {
    padding: 0.5rem var(--space-md);
  }
  
  /* RESPONSIVE - Mobiles */
  @media (max-width: 480px) {
    padding: 0.5rem var(--space-sm); /* Moins de padding sur très petit écran */
  }
`;

// Conteneur du logo (utilise NavLink pour la navigation)
const LogoContainer = styled(NavLink)`
  font-size: var(--font-size-xl);     /* Taille de police grande */
  font-weight: 700;                   /* Texte en gras */
  color: var(--primary);              /* Couleur principale */
  display: flex;
  align-items: center;                /* Alignement vertical */
  text-decoration: none;              /* Pas de soulignement */
  min-width: 80px;                    /* Largeur minimale */
  padding-right: 10px;                /* Espace à droite */
  justify-content: flex-start;        /* Alignement à gauche */

  /* Styles pour l'image du logo */
  img {
    height: 60px;                     /* Hauteur fixe */
    width: auto;                      /* Largeur automatique pour garder ratio */
    max-width: 80px;                  /* Largeur maximale */
    object-fit: contain;              /* Garder les proportions */
    margin-left: 0;
  }

  /* RESPONSIVE - Écrans moyens */
  @media (max-width: 1200px) {
    min-width: 80px;
    padding-right: 10px;
    img {
      max-width: 80px;
    }
  }

  /* RESPONSIVE - Tablettes et mobiles */
  @media (max-width: 768px) {
    min-width: 80px;
    padding-right: 10px;
    img {
      height: 60px;
      max-width: 80px;
    }
  }
`;

const PopupButtons = styled.div`
  position: absolute;
  bottom: -60px;
  left: 0;
  display: flex;
  gap: 10px;
  background: white;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 150px;
  }
`;

const PopupButton = styled.button`
  padding: 8px 15px;
  background: linear-gradient(135deg, #0066cc, #004999);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;

  /* Menu desktop : visible quand il y a la place */
  &.desktop-menu {
    display: flex;
    
    ${({ $isCompact }) => $isCompact && `
      position: absolute;
      left: -9999px;
      top: -9999px;
      visibility: hidden;
      pointer-events: none;
    `}

    @media (max-width: 991px) {
      display: none;
    }
  }

  /* Menu mobile slide : caché par défaut, slide de la droite sur mobile */
  &.mobile-slide-menu {
    position: fixed;
    top: 0;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
    bottom: 0; /* Utilise bottom: 0 au lieu de height: 100vh pour garantir la hauteur complète */
    width: 280px;
    height: 100vh; /* Fallback pour navigateurs très anciens */
    height: 100dvh; /* Hauteur dynamique moderne qui s'adapte aux barres UI mobiles */
    min-height: 100vh; /* Hauteur minimale de sécurité pour navigateurs anciens */
    min-height: 100dvh; /* Hauteur dynamique moderne qui s'adapte aux barres UI mobiles */
    max-height: none; /* Supprime la limitation de hauteur maximale */
    background-color: var(--white);
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 2rem;
    transition: right var(--transition-normal);
    box-shadow: var(--shadow-lg);
    z-index: 9999999; /* Z-index très élevé pour être au-dessus de tout, même des lightbox */
    display: flex;
    overflow-y: auto; /* Permet le scroll si nécessaire */
    
    /* Force la hauteur même si le body a overflow: hidden */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      background: transparent;
    }
  }
`;

const NavItem = styled.div`
  position: relative;
  margin: 0 var(--space-md);

  @media (max-width: 991px) {
    margin: var(--space-md) 0;
    width: 100%;
  }
`;

const StyledNavLink = styled(NavLink)`
  color: var(--gray-dark);
  font-weight: 500;
  padding: var(--space-xs) var(--space-sm);
  transition: color var(--transition-fast);
  position: relative;
  text-decoration: none;
  display: inline-block;
  white-space: nowrap;

  &:after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 0;
    height: 2px;
    background: #0066cc;
    transition: width var(--transition-normal);
  }

  &:hover, &.active {
    color: #0066cc;
    
    &:after {
      width: 100%;
    }
  }

  @media (max-width: 991px) {
    display: block;
    padding: var(--space-xs) 0;
  }
`;

const ScrollLink = styled.a`
  color: var(--gray-dark);
  font-weight: 500;
  padding: var(--space-xs) var(--space-sm);
  transition: color var(--transition-fast);
  position: relative;
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  white-space: nowrap;

  &:after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 0;
    height: 2px;
    background: #0066cc;
    transition: width var(--transition-normal);
  }

  &:hover, &.active {
    color: #0066cc;
    
    &:after {
      width: 100%;
    }
  }

  @media (max-width: 991px) {
    display: block;
    padding: var(--space-xs) 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Les icônes ne doivent jamais être compressées */
  gap: 0.5rem; /* Espacement compact */
  
  @media (max-width: 480px) {
    gap: 0.25rem; /* Encore plus compact sur très petit écran */
  }
`;

const IconButton = styled.button`
  background: transparent;
  color: var(--gray-dark);
  font-size: var(--font-size-md);
  margin-left: 0; /* Supprime les marges car on utilise gap sur ActionButtons */
  padding: var(--space-xs);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  position: relative;
  min-width: 40px; /* Taille minimale */
  
  @media (max-width: 480px) {
    min-width: 35px; /* Plus compact sur très petit écran */
    font-size: 1rem;
    padding: 0.25rem;
  }

  &:hover {
    color: var(--primary);
    background: transparent;
    transform: translateY(0);
    box-shadow: none;
  }
`;

// Animation rebond (même style que le bouton flottant PC) pour le bouton sondage mobile
const sondageBounce = keyframes`
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(5deg); }
  20% { transform: rotate(-5deg); }
  25%, 100% { transform: rotate(0deg); }
`;

const SondageIconButton = styled(IconButton)`
  margin-right: 10px;
  padding: 0;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    ${props => props.$hasActivePoll && css`
      animation: ${sondageBounce} 2.5s ease-in-out infinite;
    `}
  }
  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
    min-width: 44px;
    min-height: 44px;
  }
`;

const MobileMenuButton = styled.button`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  background: transparent;
  color: var(--gray-dark);
  font-size: 1.5rem;
  padding: 0.5rem;
  min-width: 40px; /* Taille minimale */
  
  @media (max-width: 480px) {
    min-width: 35px; /* Plus compact sur très petit écran */
    font-size: 1.25rem;
    padding: 0.25rem;
  }
  
  &:hover {
    color: var(--primary);
    background: transparent;
    transform: translateY(0);
    box-shadow: none;
  }

  @media (max-width: 991px) {
    display: block;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: opacity var(--transition-normal), visibility var(--transition-normal);
  z-index: var(--z-overlay);
`;

const CloseMenuButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  color: var(--gray-dark);
  font-size: 1.5rem;
  padding: 0.5rem;
  display: none;
  
  &:hover {
    color: var(--primary);
    background: transparent;
    transform: translateY(0);
    box-shadow: none;
  }

  @media (max-width: 991px) {
    display: block;
  }
`;

const SearchContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  margin-left: 0; /* Supprime marge car on utilise gap */
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 150px; /* Réduit sur tablette */
  }
  
  @media (max-width: 480px) {
    min-width: 100px; /* Encore plus compact sur mobile */
  }
`;

const SearchInput = styled(motion.input)`
  position: absolute;
  right: 40px;
  width: ${props => props.$isOpen ? '200px' : '0'};
  padding: ${props => props.$isOpen ? '8px 12px' : '8px 0'};
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  opacity: ${props => props.$isOpen ? '1' : '0'};
  
  @media (max-width: 768px) {
    width: ${props => props.$isOpen ? '150px' : '0'}; /* Plus étroit sur tablette */
  }
  
  @media (max-width: 480px) {
    width: ${props => props.$isOpen ? '100px' : '0'}; /* Très compact sur mobile */
    right: 35px; /* Ajuste la position */
    font-size: 0.8rem;
  }
  
  &:focus {
    outline: none;
    border-color: #0066cc;
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
  }
`;

const SearchResults = styled(motion.div)`
  position: absolute;
  top: 45px;
  right: 0;
  width: 350px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  z-index: 9999;
  padding: 10px;
  border: 1px solid #e0e0e0;
  
  @media (max-width: 768px) {
    width: 300px;
  }
  
  @media (max-width: 480px) {
    width: 280px;
  }
`;

const ResultItem = styled.div`
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  
  &:hover {
    background-color: #f5f7fa;
    transform: translateX(5px);
  }

  &:last-child {
    border-bottom: none;
  }
  
  /* Empêcher la coupure des mots */
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
`;

const ResultIcon = styled.div`
  color: #0066cc;
  font-size: 1.2rem;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ResultContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  
  /* Empêcher la coupure des mots */
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
`;

const ResultTitle = styled.h4`
  font-size: 0.9rem;
  color: #333;
  margin: 0 0 5px 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.3;
  flex-wrap: wrap;
  
  mark {
    font-weight: 700;
    white-space: nowrap;
    display: inline;
  }
  
  /* Empêcher la coupure des mots */
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
`;

const ResultType = styled.span`
  font-size: 0.7rem;
  color: #666;
  background: #eee;
  padding: 2px 6px;
  border-radius: 10px;
`;

const ResultDescription = styled.p`
  font-size: 0.8rem;
  color: #666;
  margin: 5px 0 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ResultMeta = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin: 4px 0 0 0;
  line-height: 1.4;
  
  span {
    display: block;
    margin: 2px 0;
  }
`;

const NoResults = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9rem;
`;

const LoadingSpinner = styled.div`
  padding: 20px;
  text-align: center;
  color: #0066cc;
  font-size: 0.9rem;
`;

const IconMap = {
  'FaNewspaper': FaNewspaper,
  'FaCalendarAlt': FaCalendarAlt,
  'FaUsers': FaUsers,
  'FaBook': FaBook,
  'FaHandshake': FaHandshake,
  'FaFileAlt': FaFileAlt
};

// Add
const typeToDataAttribute = {
  'Événement': 'event',
  'Club': 'club',
  'Livre': 'book',
  'Actualité': 'news',
  'Partenaire': 'partner'
};

// Fonction pour obtenir l'image d'une entité ou l'icône par défaut
const getEntityImage = (entity) => {
  // Essayer de récupérer l'image selon la structure Strapi v5
  let imageUrl = null;
  
  // Vérifier différentes structures possibles pour l'image
  if (entity.image?.data?.attributes?.url) {
    imageUrl = entity.image.data.attributes.url;
  } else if (entity.Image?.data?.attributes?.url) {
    imageUrl = entity.Image.data.attributes.url;
  } else if (entity.attributes?.image?.data?.attributes?.url) {
    imageUrl = entity.attributes.image.data.attributes.url;
  } else if (entity.attributes?.Image?.data?.attributes?.url) {
    imageUrl = entity.attributes.Image.data.attributes.url;
  } else if (entity.logo?.data?.attributes?.url) {
    imageUrl = entity.logo.data.attributes.url;
  } else if (entity.Logo?.data?.attributes?.url) {
    imageUrl = entity.Logo.data.attributes.url;
  } else if (entity.attributes?.logo?.data?.attributes?.url) {
    imageUrl = entity.attributes.logo.data.attributes.url;
  } else if (entity.attributes?.Logo?.data?.attributes?.url) {
    imageUrl = entity.attributes.Logo.data.attributes.url;
  }
  
  // Si on a une image, retourner l'URL complète
  if (imageUrl) {
    // Si c'est déjà une URL complète, la retourner
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    // Sinon, construire l'URL complète
    return `https://api.offisport.com${imageUrl}`;
  }
  
  // Si pas d'image, retourner l'icône SVG par défaut selon le type
  const type = entity.type || 'activity';
  const defaultIcons = {
    'Actualité': '/images/defaults/news-default.svg',
    'Événement': '/images/defaults/event-default.svg',
    'Club': '/images/defaults/club-default.svg',
    'Livre': '/images/defaults/book-default.svg',
    'Partenaire': '/images/defaults/partner-default.svg',
    'Activité': '/images/defaults/activity-default.svg'
  };
  
  return defaultIcons[type] || '/images/defaults/activity-default.svg';
};



const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('accueil');
  const [showAboutPopup, setShowAboutPopup] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [accueilData, setAccueilData] = useState(null);
  const [isCompactMenu, setIsCompactMenu] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  // Ajouter les états pour la popup d'activité
  const [showActivityPopup, setShowActivityPopup] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [hasActivePoll, setHasActivePoll] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const navContainerRef = useRef(null);
  const logoRef = useRef(null);
  const desktopMenuRef = useRef(null);
  const actionButtonsRef = useRef(null);
  const { setActiveSection: setNavigationActiveSection } = useNavigation();

  // Détection d'appareil
  const deviceInfo = useDeviceDetection();

  // Récupérer les données d'accueil pour le logo
  useEffect(() => {
    const fetchAccueilData = async () => {
      try {
        const data = await getAccueil();
        setAccueilData(data);
      } catch (error) {
        console.error('Erreur lors de la récupération des données d\'accueil pour le logo:', error);
      }
    };

    fetchAccueilData();
  }, []);

  // Vérifier si un sondage est en cours (pour animation du bouton mobile)
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await SimplePollService.getCurrentPoll();
        if (!cancelled && res?.success && res?.poll) setHasActivePoll(true);
      } catch (e) {
        // ignore
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // Détection d'espace disponible pour basculer desktop <-> burger
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => {
        const smallViewport = window.innerWidth <= 991;
        setIsSmallViewport(smallViewport);
        setIsCompactMenu(smallViewport);
      };

      window.addEventListener('resize', handleResize);
      handleResize();

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    let rafId = null;
    const updateCompactState = () => {
      if (!navContainerRef.current) return;

      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const containerWidth = navContainerRef.current?.getBoundingClientRect().width || 0;
        const logoWidth = logoRef.current?.getBoundingClientRect().width || 0;
        const menuWidth = desktopMenuRef.current?.getBoundingClientRect().width || 0;
        const actionsWidth = actionButtonsRef.current?.getBoundingClientRect().width || 0;
        const smallViewport = window.innerWidth <= 991;
        const safetyPadding = 24;

        setIsSmallViewport(smallViewport);

        const shouldCompact = (menuWidth === 0 || containerWidth === 0)
          ? smallViewport
          : (logoWidth + menuWidth + actionsWidth + safetyPadding > containerWidth);

        setIsCompactMenu(prev => (prev !== shouldCompact ? shouldCompact : prev));
      });
    };

    const resizeObserver = new ResizeObserver(updateCompactState);
    [navContainerRef.current, logoRef.current, desktopMenuRef.current, actionButtonsRef.current]
      .filter(Boolean)
      .forEach((element) => resizeObserver.observe(element));

    window.addEventListener('resize', updateCompactState);
    updateCompactState();

    return () => {
      window.removeEventListener('resize', updateCompactState);
      resizeObserver.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Si on repasse en mode desktop, fermer le menu mobile
  useEffect(() => {
    if (!isCompactMenu && !isSmallViewport && isOpen) {
      setIsOpen(false);
    }
  }, [isCompactMenu, isSmallViewport, isOpen]);



  // DEBUG : Affiche les données récupérées depuis Strapi


  // L'URL du logo a déjà été transformée par getAccueil()
  const logoUrl = accueilData?.Logo?.url;

  // Données de secours pour la démo
  const demoData = {
    actualites: [
      {
        id: 1,
        attributes: {
          titre: "Tournoi de foot annuel",
          description: "Le grand tournoi de football annuel aura lieu le mois prochain. Inscriptions ouvertes !"
        }
      },
      {
        id: 2,
        attributes: {
          titre: "Nouveau terrain de foot",
          description: "Inauguration du nouveau terrain synthétique ce weekend"
        }
      }
    ],
    evenements: [
      {
        id: 1,
        attributes: {
          titre: "Match de foot amical",
          description: "Match amical entre les équipes seniors et juniors",
          lieu: "Stade municipal"
        }
      }
    ],
    clubs: [
      {
        id: 1,
        attributes: {
          nom: "Club de Foot OSARC",
          description: "Club de football pour tous les âges",
          ville: "Orléans"
        }
      }
    ],
    bibliotheque: [
      {
        id: 1,
        attributes: {
          titre: "Histoire du Football",
          auteur: "Jean Dupont",
          description: "Un livre complet sur l'histoire du football français"
        }
      }
    ],
    partenaires: [
      {
        id: 1,
        attributes: {
          nom: "FootShop",
          description: "Magasin spécialisé dans l'équipement de football"
        }
      }
    ],
    sondages: [
      {
        id: 1,
        attributes: {
          titre: "Préférence sportive",
          description: "Quel sport préférez-vous ? Football, Basketball ou Tennis ?"
        }
      }
    ]
  };

  useEffect(() => {
    if (location.pathname.startsWith('/bibliotheque')) {
      setNavigationActiveSection('bibliotheque');
    } else if (location.pathname.startsWith('/actualites')) {
      setNavigationActiveSection('actualites');
    } else if (location.pathname.startsWith('/activites')) {
      setNavigationActiveSection('activites');
    } else if (location.pathname.startsWith('/clubs')) {
      setNavigationActiveSection('clubs');
    } else if (location.pathname.startsWith('/evenements')) {
      setNavigationActiveSection('evenements');
    } else if (location.pathname.startsWith('/partenaires')) {
      setNavigationActiveSection('partenaires');
    } else if (location.pathname === '/' || location.pathname.startsWith('/accueil')) {
      setNavigationActiveSection('accueil');
    }
  }, [location.pathname, setNavigationActiveSection]);

  // Focus automatique sur le champ de recherche quand il s'ouvre
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      // Petit délai pour laisser l'animation se terminer
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Remplacer la logique de recherche par l'appel à searchAllEntities
  useEffect(() => {
    const search = async () => {
      if (debouncedSearchTerm.length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const data = await searchAllEntities(debouncedSearchTerm);
        
        // On regroupe tous les résultats dans un tableau à plat avec type et icône
        const resultsFlat = [];
        if (data.actualites) {
          resultsFlat.push(...data.actualites.map(item => ({
            ...item,
            type: 'Actualité',
            icon: 'FaNewspaper',
            attributes: item.attributes || item
          })));
        }
        if (data.evenements) {
          resultsFlat.push(...data.evenements.map(item => ({
            ...item,
            type: 'Événement',
            icon: 'FaCalendarAlt',
            attributes: item.attributes || item
          })));
        }
        if (data.clubs) {
          resultsFlat.push(...data.clubs.map(item => ({
            ...item,
            type: 'Club',
            icon: 'FaUsers',
            attributes: item.attributes || item
          })));
        }
        if (data.livres) {
          resultsFlat.push(...data.livres.map(item => ({
            ...item,
            type: 'Livre',
            icon: 'FaBook',
            attributes: item.attributes || item
          })));
        }
        if (data.partenaires) {
          resultsFlat.push(...data.partenaires.map(item => ({
            ...item,
            type: 'Partenaire',
            icon: 'FaHandshake',
            attributes: item.attributes || item
          })));
        }
        if (data.activites) {
          resultsFlat.push(...data.activites.map(item => ({
            ...item,
            type: 'Activité',
            icon: 'FaFileAlt',
            attributes: item.attributes || item
          })));
        }
        
        setResults(resultsFlat);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    search();
  }, [debouncedSearchTerm]);

  // Fonction pour mettre en surbrillance le mot recherché
  const highlightSearchTerm = (text, searchTerm) => {
    if (!text || !searchTerm) return text;
    
    // Échapper les caractères spéciaux dans searchTerm pour le regex
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    
    // Utiliser replace pour surligner en bleu simple
    const highlightedText = text.replace(regex, (match) => {
      return `<span style="color: #1976d2; font-weight: bold;">${match}</span>`;
    });
    
    // Retourner le HTML avec dangerouslySetInnerHTML
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Fonction pour nettoyer le HTML et les caractères spéciaux
  const cleanHtml = (htmlString) => {
    if (!htmlString) return '';
    
    // Supprimer les balises HTML
    let cleanText = htmlString.replace(/<[^>]*>/g, '');
    
    // Décoder les entités HTML courantes
    cleanText = cleanText
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&eacute;/g, 'é')
      .replace(/&egrave;/g, 'è')
      .replace(/&agrave;/g, 'à')
      .replace(/&ccedil;/g, 'ç');
    
    // Supprimer les espaces multiples mais préserver les mots
    cleanText = cleanText.replace(/\s+/g, ' ').trim();
    
    // S'assurer qu'on ne coupe pas au milieu d'un mot
    return cleanText;
  };

  // Fonction pour extraire un extrait pertinent avec le mot recherché
  const getRelevantExcerpt = (text, searchTerm, maxLength = 150) => {
    if (!text || !searchTerm) {
      const cleanText = cleanHtml(text);
      return cleanText ? cleanText.substring(0, maxLength) + '...' : '';
    }
    
    const cleanText = cleanHtml(text);
    if (!cleanText) return '';
    
    const lowerText = cleanText.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearchTerm);
    
    if (index === -1) {
      return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
    }
    
    // Trouver le début d'un mot complet
    let start = Math.max(0, index - 50);
    while (start > 0 && cleanText[start] !== ' ' && cleanText[start] !== '.' && cleanText[start] !== ',') {
      start--;
    }
    if (start > 0) start++; // Avancer d'un caractère pour ne pas inclure l'espace
    
    // Trouver la fin d'un mot complet
    let end = Math.min(cleanText.length, index + searchTerm.length + 100);
    while (end < cleanText.length && cleanText[end] !== ' ' && cleanText[end] !== '.' && cleanText[end] !== ',') {
      end++;
    }
    
    let excerpt = cleanText.substring(start, end);
    
    // Ajouter "..." si on ne commence pas au début
    if (start > 0) excerpt = '...' + excerpt;
    if (end < cleanText.length) excerpt = excerpt + '...';
    
    return excerpt;
  };

  // Gestionnaire pour le clic en dehors du conteneur de recherche
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearch(false);
        setSearchTerm('');
        setResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Gestionnaire pour la touche Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowSearch(false);
        setSearchTerm('');
        setResults([]);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };
  
  const handleNavigation = (section) => {
    setActiveSection(section);
    navigate(`/${section}`);
  };

  // Adapter handleResultClick pour ouvrir la popup d'activité selon le type
  const handleResultClick = (result) => {
    setShowSearch(false);
    setSearchTerm('');
    setResults([]);
    if (result.type === 'Actualité') {
      navigate(`/actualites/${result.id}`);
    } else if (result.type === 'Événement') {
      navigate(`/evenements/${result.id}`);
    } else if (result.type === 'Club') {
      navigate(`/clubs/${result.id}`);
    } else if (result.type === 'Partenaire') {
      navigate(`/partenaires/${result.id}`);
    } else if (result.type === 'Livre') {
      navigate(`/bibliotheque/${result.id}`);
    } else if (result.type === 'Activité') {
      setSelectedActivity(result);
      setShowActivityPopup(true);
    } else {
      const element = document.getElementById(result.link?.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Fonction pour fermer la popup d'activité
  const closeActivityPopup = () => {
    setShowActivityPopup(false);
    setSelectedActivity(null);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('menu-blur');
    } else {
      document.body.classList.remove('menu-blur');
    }
  }, [isOpen]);

  return (
    <>
      <HeaderContainer 
        className={`${scrolled ? 'scrolled' : ''} ${deviceInfo.getDeviceClasses()}`}
        style={deviceInfo.getDeviceStyles()}
      >
        <NavContainer ref={navContainerRef}>
          <LogoContainer 
            to="/" 
            ref={logoRef}
            onClick={(e) => {
              e.preventDefault();
              setShowAboutPopup(true);
            }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Logo OSARC" />
            ) : (
              <img src={LogoOsarc} alt="Logo OSARC" />
            )}
          </LogoContainer>

          {/* Menu desktop - affiché seulement s'il y a la place */}
          <NavMenu
            ref={desktopMenuRef}
            $isOpen={isOpen}
            $isCompact={isCompactMenu}
            className="desktop-menu"
            aria-hidden={isCompactMenu ? 'true' : 'false'}
          >
            <NavItem>
              <StyledNavLink to="/" end onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Accueil</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/actualites" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Actualités</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/activites" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Activités</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/evenements" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Événements</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/clubs" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Clubs</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/bibliotheque" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Bibliothèque</StyledNavLink>
            </NavItem>
            <NavItem>
              <StyledNavLink to="/partenaires" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Partenaires</StyledNavLink>
            </NavItem>
          </NavMenu>

          <ActionButtons ref={actionButtonsRef}>
            <SearchContainer ref={searchContainerRef}>
              <IconButton 
                aria-label="Rechercher" 
                onClick={() => setShowSearch(!showSearch)}
                style={{ marginRight: showSearch ? '10px' : '0' }}
              >
                <FaSearch />
              </IconButton>
              <AnimatePresence>
                {showSearch && (
                  <SearchInput
                    ref={searchInputRef}
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    $isOpen={showSearch}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 200, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </AnimatePresence>
              <AnimatePresence>
                {(showSearch && (searchTerm.length > 0 || results.length > 0)) && (
                  <SearchResults
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    {loading ? (
                      <LoadingSpinner>Recherche en cours...</LoadingSpinner>
                    ) : results.length > 0 ? (
                      results.map((result, index) => {
                        const Icon = IconMap[result.icon];
                        
                        // Récupérer le titre selon la structure Strapi v5
                        const rawTitre = result.Titre || result.titre || result.attributes?.Titre || result.attributes?.titre || result.attributes?.nom || result.Nom || result.nom || 'Sans titre';
                        const titre = cleanHtml(rawTitre);
                        
                        // Récupérer la description
                        const description = result.Description || result.description || result.attributes?.Description || result.attributes?.description || '';
                        
                        // Créer l'extrait pertinent seulement si on a une description
                        const excerpt = description ? getRelevantExcerpt(description, searchTerm) : '';
                        
                        // Pour les livres, récupérer l'auteur et l'éditeur
                        const isLivre = result.type === 'Livre';
                        const auteur = isLivre ? (result.Auteur || result.auteur || result.attributes?.Auteur || result.attributes?.auteur || '') : '';
                        const editeur = isLivre ? (result.Editeur || result.editeur || result.attributes?.Editeur || result.attributes?.editeur || '') : '';
                        
                        // Obtenir l'image ou l'icône par défaut
                        const imageUrl = getEntityImage(result);
                        const isDefaultIcon = imageUrl.includes('/images/defaults/');
                        
                        return (
                          <ResultItem
                            key={index}
                            onClick={() => handleResultClick(result)}
                          >
                            <ResultIcon>
                              {isDefaultIcon ? (
                                Icon ? <Icon /> : <img src={imageUrl} alt={`Icône ${result.type}`} />
                              ) : (
                                <img src={imageUrl} alt={titre} />
                              )}
                            </ResultIcon>
                            <ResultContent>
                              <ResultTitle>
                                <ResultType>{result.type}</ResultType>
                                {highlightSearchTerm(titre, searchTerm)}
                              </ResultTitle>
                              {isLivre && (auteur || editeur) && (
                                <ResultMeta>
                                  {auteur && (
                                    <span>Auteur : {highlightSearchTerm(auteur, searchTerm)}</span>
                                  )}
                                  {editeur && (
                                    <span>Éditeur : {highlightSearchTerm(editeur, searchTerm)}</span>
                                  )}
                                </ResultMeta>
                              )}
                              {excerpt && !isLivre && (
                                <ResultDescription>
                                  {highlightSearchTerm(excerpt, searchTerm)}
                                </ResultDescription>
                              )}
                            </ResultContent>
                          </ResultItem>
                        );
                      })
                    ) : searchTerm.length >= 2 ? (
                      <NoResults>Aucun résultat trouvé pour "{searchTerm}"</NoResults>
                    ) : null}
                  </SearchResults>
                )}
              </AnimatePresence>
            </SearchContainer>
            {/* Bouton sondage mobile - même icône et animation rebond que sur PC, en plus petit */}
            {deviceInfo.renderOnMobile(
              <SondageIconButton 
                aria-label="Sondage Club du mois" 
                $hasActivePoll={hasActivePoll}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openSondage'));
                }}
              >
                <img src={ClubDuMoisLogo} alt="Club du mois" />
              </SondageIconButton>
            )}
            {/* Menu burger - affiché quand le menu desktop ne rentre pas */}
            <MobileMenuButton
              className="mobile-menu-button"
              onClick={toggleMenu}
              $isVisible={isCompactMenu}
            >
              <FaBars />
            </MobileMenuButton>
          </ActionButtons>
        </NavContainer>
      </HeaderContainer>
      
      {/* Overlay sous le menu burger mobile */}
      {isOpen && (
        <div className="menu-overlay" onClick={closeMenu} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.2)',
          zIndex: 998,
          transition: 'background 0.3s',
        }} />
      )}
      
      {/* Menu burger mobile qui s'ouvre en overlay */}
      <NavMenu $isOpen={isOpen} className="mobile-slide-menu" style={isOpen ? { zIndex: 999 } : {}}>
        <CloseMenuButton onClick={closeMenu}>
          <FaTimes />
        </CloseMenuButton>

        <NavItem>
          <StyledNavLink to="/" end onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Accueil</StyledNavLink>
        </NavItem>
        <NavItem>
          <StyledNavLink to="/actualites" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Actualités</StyledNavLink>
        </NavItem>
        <NavItem>
          <StyledNavLink to="/activites" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Activités</StyledNavLink>
        </NavItem>
        <NavItem>
          <StyledNavLink to="/evenements" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Événements</StyledNavLink>
        </NavItem>
        <NavItem>
          <StyledNavLink to="/clubs" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Clubs</StyledNavLink>
        </NavItem>
        <NavItem>
          <StyledNavLink to="/bibliotheque" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Bibliothèque</StyledNavLink>
        </NavItem>
          <NavItem>
            <StyledNavLink to="/partenaires" onClick={e => { closeMenu(); window.scrollTo(0,0); }}>Partenaires</StyledNavLink>
          </NavItem>
        </NavMenu>
      
      <AboutPopupSidebar 
        isOpen={showAboutPopup} 
        onClose={() => setShowAboutPopup(false)}
      />

      {showActivityPopup && selectedActivity && (
        <ArticleCard
          forcePopupOpen={true}
          title={selectedActivity.title || selectedActivity.attributes?.titre || ''}
          category={selectedActivity.category || selectedActivity.attributes?.category || ''}
          excerpt={selectedActivity.excerpt || selectedActivity.attributes?.extrait || ''}
          image={selectedActivity.image || selectedActivity.attributes?.image?.data?.attributes?.url || ''}
          podium={selectedActivity.podium || selectedActivity.attributes?.podium || null}
          laureat={selectedActivity.laureat || selectedActivity.attributes?.laureat || null}
          labels={selectedActivity.labels || selectedActivity.attributes?.labels || null}
          onClose={closeActivityPopup}
        />
      )}
    </>
  );
};

export default Navbar; 